import { User, Phone, Mail, MapPin, Store, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatPhone } from '@/lib/formatters';

interface ShipmentPartyCardProps {
  title: string;
  name?: string | null;
  phone?: string | null;
  alternativePhone?: string | null;
  email?: string | null;
  storeName?: string | null;
  address?: {
    street: string;
    neighborhood: string;
    city: string;
    lat?: number;
    lng?: number;
  };
  notes?: string | null;
  onMapClick?: () => void;
  icon?: 'user' | 'store' | 'building';
  className?: string;
}

export function ShipmentPartyCard({
  title,
  name,
  phone,
  alternativePhone,
  email,
  storeName,
  address,
  notes,
  onMapClick,
  icon = 'user',
  className,
}: ShipmentPartyCardProps) {
  const IconComponent = icon === 'store' ? Store : icon === 'building' ? Building : User;

  return (
    <Card className={cn("h-full", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <IconComponent className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">الاسم:</span>
          <span className="text-sm font-medium">{name || '-'}</span>
        </div>

        {phone && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">الهاتف:</span>
            <span className="text-sm font-medium flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              {formatPhone(phone)}
            </span>
          </div>
        )}
        
        {alternativePhone && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">هاتف بديل:</span>
            <span className="text-sm font-medium flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              {formatPhone(alternativePhone)}
            </span>
          </div>
        )}
        
        {email && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">الإيميل:</span>
            <span className="text-sm font-medium flex items-center gap-1.5 truncate max-w-[180px]">
              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
              {email}
            </span>
          </div>
        )}
        
        {storeName && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">المتجر:</span>
            <span className="text-sm font-medium flex items-center gap-1.5">
              <Store className="h-3.5 w-3.5" />
              {storeName}
            </span>
          </div>
        )}
        
        {address && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">العنوان:</span>
              {address.lat && address.lng && onMapClick && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onMapClick}
                  className="h-auto p-1 hover:bg-primary/10"
                >
                  <MapPin className="h-4 w-4 text-primary" />
                </Button>
              )}
            </div>
            <p className="text-sm">
              {[address.street, address.neighborhood, address.city].filter(Boolean).join('، ')}
            </p>
          </div>
        )}
        
        {notes && (
          <div className="pt-2 border-t">
            <span className="text-sm text-muted-foreground">ملاحظات:</span>
            <p className="text-sm mt-1">{notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
